import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../../common/header.js';
import { ApiError } from '../../common/apiError.js';
import LoadingComp from '../../common/loading.js';
import { getEmailSignatureTemplate } from './emailSignatureApi.js';
import { signatureHtml } from './emailSignatureModel.js';
import SignaturePreview from './SignaturePreview.js';
import './EmailSignature.css';

const TOAST_CONFIG = { position: 'top-right', autoClose: 4000 };

/** Collect variable values and copy a generated rich-text signature for an email client. */
export default function EmailSignatureTool() {
    const [template, setTemplate] = useState(null);
    const [values, setValues] = useState({});
    const [generated, setGenerated] = useState(false);
    const [status, setStatus] = useState('loading');
    const previewRef = useRef(null);

    useEffect(() => {
        let active = true;
        getEmailSignatureTemplate().then(result => {
            if (!active) return;
            setTemplate(result); setStatus(result ? 'ready' : 'empty');
        }).catch(() => { if (active) setStatus('error'); });
        return () => { active = false; };
    }, []);

    /** Copy both HTML and plain text when supported, with a selection-based fallback. */
    async function copySignature() {
        try {
            const html = signatureHtml(template, values);
            const plain = previewRef.current?.innerText || '';
            if (window.ClipboardItem && navigator.clipboard?.write) {
                await navigator.clipboard.write([new window.ClipboardItem({ 'text/html': new Blob([html], { type: 'text/html' }), 'text/plain': new Blob([plain], { type: 'text/plain' }) })]);
            } else {
                const range = document.createRange(); range.selectNodeContents(previewRef.current);
                const selection = window.getSelection(); selection.removeAllRanges(); selection.addRange(range);
                if (!document.execCommand('copy')) throw new Error('Copy failed');
                selection.removeAllRanges();
            }
            toast.success('✅ Signature copied. Paste it into your email client.', TOAST_CONFIG);
        } catch (error) {
            toast.error('❌ The signature could not be copied. Highlight the preview and copy it manually.', TOAST_CONFIG);
        }
    }

    if (status === 'loading') return <LoadingComp />;
    if (status === 'error') return <ApiError message='The email signature configuration could not be loaded.' />;
    if (status === 'empty') return <ApiError message='Your school administrator has not configured an email signature yet.' />;
    return <div className='main_div'><div className='internal_main_div email-signature-container'>
        <Header title='Email Signature Generator' subtitle='Enter your details, generate the signature, then copy it into your email client.' rightContent={<Link className='module-page-header__button' to='/app/tools'>Back to Tools</Link>} />
        <section className='email-signature-card'><h2>Your details</h2><div className='email-signature-fields'>
            {template.variables.map(variable => <label key={variable.key}><span>{variable.label}</span><input type='text' maxLength='500' value={values[variable.key] || ''} placeholder={variable.placeholder || ''} onChange={event => { setValues(current => ({ ...current, [variable.key]: event.target.value })); setGenerated(false); }} /></label>)}
        </div><button className='email-signature-primary' type='button' onClick={() => setGenerated(true)}>Generate signature</button></section>
        {generated && <section className='email-signature-card'><div className='email-signature-preview-heading'><h2>Completed signature</h2><button className='email-signature-primary' type='button' onClick={copySignature}>Copy signature</button></div><p className='email-signature-help'>You can also highlight the signature below and copy it manually.</p><SignaturePreview previewRef={previewRef} template={template} values={values} /></section>}
    </div></div>;
}
